import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, X, Play, Sparkles } from "lucide-react";

const VIDEO_SRC = "/PROMO_ESTUDIO.mp4";

// Sem prazo — mostra sempre. Para desativar, muda para `return false`.
export function isPromoBannerActive() {
  return true;
}

export default function PromoBanner({ onCta }) {
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("promo_banner_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const videoRef = useRef(null);

  useEffect(() => {
    if (!isPromoBannerActive() || dismissed) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.then === "function") {
      p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [dismissed]);

  const dismiss = () => {
    try {
      sessionStorage.setItem("promo_banner_dismissed", "1");
    } catch {}
    setDismissed(true);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  if (!isPromoBannerActive() || dismissed) return null;

  return (
    <section
      className="relative bg-black border-b-2 border-yellow-400 overflow-hidden"
      data-testid="promo-banner"
    >
      {/* Yellow accent glow */}
      <div
        className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[400px] blur-3xl opacity-[0.14]"
        style={{ background: "radial-gradient(circle, #ffd60a 0%, transparent 65%)" }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 md:top-4 md:right-4 z-20 text-neutral-500 hover:text-yellow-400 transition-colors"
          data-testid="promo-banner-close"
          aria-label="Fechar banner"
        >
          <X size={20} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center">
          {/* Video (vertical) */}
          <div className="relative mx-auto w-[220px] md:w-[260px] aspect-[9/16] rounded-sm overflow-hidden border border-yellow-400/40 shadow-[0_20px_60px_-15px_rgba(255,214,10,0.35)]">
            <video
              ref={videoRef}
              src={VIDEO_SRC}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              data-testid="promo-banner-video"
            />
            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              className="absolute bottom-2 right-2 bg-black/70 hover:bg-black text-yellow-400 p-2 rounded-full transition-colors"
              data-testid="promo-banner-mute-toggle"
              aria-label={muted ? "Ativar som" : "Silenciar"}
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            {!playing ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                <Play size={40} className="text-yellow-400" />
              </div>
            ) : null}
          </div>

          {/* Text + CTA */}
          <div>
            <div className="flex items-center gap-3 text-yellow-400 text-[10px] uppercase tracking-[0.32em] font-black mb-3">
              <span className="w-8 h-[2px] bg-yellow-400" />
              <Sparkles size={14} />
              <span>Estúdio Premium · Novidade</span>
            </div>

            <h2
              className="font-display text-3xl md:text-5xl leading-[0.98] tracking-tighter uppercase text-white"
              data-testid="promo-banner-title"
            >
              Vais continuar<br />
              a deixar a tua<br />
              <span className="text-yellow-400">saúde para depois?</span>
            </h2>

            <p className="mt-4 text-neutral-300 text-sm md:text-base max-w-xl leading-relaxed">
              Treino <b className="text-white">exclusivo</b>, num espaço{" "}
              <b className="text-white">único</b>, na Quinta do Conde.
              Marca já o teu <b className="text-yellow-400">treino experimental gratuito</b>.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onCta}
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm px-6 py-3.5 transition-colors"
                data-testid="promo-banner-cta"
              >
                Marcar Sessão Grátis →
              </button>
              <a
                href={VIDEO_SRC}
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 hover:text-yellow-300 text-xs uppercase tracking-widest font-bold underline underline-offset-4"
                data-testid="promo-banner-video-link"
              >
                Ver vídeo
              </a>
              <a
                href={VIDEO_SRC}
                download="O-MEU-PT_Promo.mp4"
                className="text-neutral-300 hover:text-yellow-400 text-xs uppercase tracking-widest font-bold underline underline-offset-4"
                data-testid="promo-banner-video-download"
              >
                Descarregar
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
